/*
 * Accessibility adapter for the Kiwi Size Chart app.
 *
 * Kiwi gets some of this right already: the modal carries role="dialog" and
 * aria-modal="true", focus is moved to .kiwi-sizing-modal-inner on open, and Escape
 * closes it. What is missing is everything that makes it operable:
 *
 *   1. The "Size Chart" trigger is a bare <span>, so it is not in the tab order at all
 *      and a keyboard user can never open the chart (WCAG 2.1.1).
 *   2. The close control is a bare <span>, so it cannot be reached or activated either.
 *   3. Tab is not trapped, so it leaks into the page header after one or two presses.
 *   4. The rest of the page stays reachable by screen reader gesture navigation.
 *   5. Focus is not returned to the trigger when the dialog closes (WCAG 2.4.3).
 *   6. The closed dialog, hidden container, and injected calculator keep YouTube
 *      iframes and carousel controls in the tab order (visibility:hidden does not
 *      always skip iframe internals). Keyboard users then tab through ~13 silent
 *      stops, can play/pause/fullscreen a video, and never see a focus ring.
 *
 * Kiwi toggles kiwi-sizing-modal-visible on <html>, which is used here as the open
 * signal. Everything else this touches is markup Kiwi documents publicly for custom CSS.
 */
(function () {
  'use strict';

  var MODAL = '.kiwi-sizing-modal';
  var INNER = '.kiwi-sizing-modal-inner';
  var CLOSE = '.ks-modal-close';
  var OPEN_CLASS = 'kiwi-sizing-modal-visible';
  var TRIGGERS = '.ks-chart-modal-link, .ks-calculator-modal-link';
  var APP_BLOCK = '.ks-container-app-block';
  // Kiwi's own furniture, which must stay interactive alongside the dialog.
  var KEEP_LIVE = '.kiwi-sizing-modal, .ks-modal-backdrop, .kiwi-sizing-modal-hidden-container';
  var CLOSED_CHROME = KEEP_LIVE;
  // The calculator's own on-page widget, not dialog chrome - it must stay clickable.
  // Only its inner media (iframes/video) should drop out of the tab order.
  var INJECT = '.ks-calculator-inject';

  var MODAL_TABS = '.ks-modal-tab, .ks-tab-header';
  var MODAL_PLAY = '.ks-video-play-button';
  var MODAL_CAROUSEL = '.kw-slide-next, .kw-slide-prev, .ks-carousel-left-btn, .ks-carousel-right-btn';

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([type=hidden]):not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex^="-"])',
    'audio[controls]', 'video[controls]', 'summary',
    '[contenteditable]:not([contenteditable="false"])',
  ].join(',');

  // Baseline since 2023, but old iOS Safari and old Firefox still lack it, and there
  // assigning .inert is a silent no-op rather than an error - so it has to be detected
  // rather than attempted.
  var SUPPORTS_INERT = 'inert' in HTMLElement.prototype;

  var lastTrigger = null;
  var openModal = null;
  var inerted = [];
  var hidden = [];

  function isVisible(el) {
    if (!el) return false;
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      if (!isVisible(el)) return false;
      var tabindex = el.getAttribute('tabindex');
      if (tabindex && tabindex.startsWith('-')) return false;
      return true;
    });
  }

  /*
   * Kiwi builds both its trigger and its close control as <span>, which no keyboard can
   * reach or activate. Promote them to real buttons. Kiwi's own click handlers stay in
   * place, so activating by keyboard just needs to synthesise the click.
   */
  function promoteToButton(el, label) {
    if (!el || el.dataset.a11yButton) return;
    el.dataset.a11yButton = '1';
    if (!el.getAttribute('role')) el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    if (label && !el.getAttribute('aria-label') && !(el.textContent || '').trim()) {
      el.setAttribute('aria-label', label);
    }
    el.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      el.click();
    });
  }

  function patchTriggers() {
    relocateTrigger();
    Array.prototype.forEach.call(document.querySelectorAll(TRIGGERS), function (el) {
      promoteToButton(el, 'Size chart');
      if (!el.getAttribute('aria-haspopup')) el.setAttribute('aria-haspopup', 'dialog');
    });
  }

  function patchModalControls() {
    var modal = document.querySelector(MODAL);
    if (!modal) return;
    promoteToButton(modal.querySelector(CLOSE), 'Close');
    Array.prototype.forEach.call(modal.querySelectorAll(MODAL_TABS), function (el) {
      promoteToButton(el);
    });
    Array.prototype.forEach.call(modal.querySelectorAll(MODAL_PLAY), function (el) {
      promoteToButton(el, 'Play video');
    });
    Array.prototype.forEach.call(modal.querySelectorAll(MODAL_CAROUSEL), function (el) {
      var next = el.className.indexOf('next') !== -1 || el.className.indexOf('right') !== -1;
      promoteToButton(el, next ? 'Next' : 'Previous');
    });
    patchTableRegions(modal);
  }

  function tableRegionLabel(el) {
    var names = [];
    var modal = el.closest(MODAL);
    var modalTab = modal && modal.querySelector('.ks-modal-tab.selected');
    if (modalTab) names.push((modalTab.textContent || '').trim());
    var panel = el.closest('.ks-tab-panel');
    if (panel && panel.id) {
      var header = document.getElementById(panel.id.replace(/^content-/, 'tab-'));
      if (header) names.push((header.textContent || '').trim());
    }
    names = names.filter(Boolean);
    return names.length ? names.join(', ') : 'Size chart';
  }

  function patchOneTableRegion(el) {
    if (!el || el.dataset.a11yTable) return;
    el.dataset.a11yTable = '1';
    el.setAttribute('tabindex', '0');
    if (el.tagName !== 'TABLE' && !el.getAttribute('role')) {
      el.setAttribute('role', 'region');
    }
    if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
      el.setAttribute('aria-label', tableRegionLabel(el));
    }
  }

  function patchTableRegions(modal) {
    if (!modal) return;
    Array.prototype.forEach.call(modal.querySelectorAll('.ks-table-wrapper'), patchOneTableRegion);
    Array.prototype.forEach.call(modal.querySelectorAll('table.ks-table'), function (el) {
      if (el.closest('.ks-table-wrapper')) return;
      patchOneTableRegion(el);
    });
  }

  /*
   * Kiwi prefers the first form[action*="/cart/add"]. The theme's installment form
   * (and the add-to-cart form) can steal the widget from the app block the merchant
   * placed after the tax line. Keep the trigger in that block so tab order matches
   * the visible layout.
   */
  function relocateTrigger() {
    var dest = document.querySelector(APP_BLOCK);
    if (!dest) return;
    // Chart and calculator triggers are separate widgets; both must be moved, or
    // whichever one Kiwi renders second is left behind under other page elements.
    Array.prototype.forEach.call(document.querySelectorAll(TRIGGERS), function (trigger) {
      if (dest.contains(trigger)) return;
      var widget = trigger.closest('.ks-container-with-modal, .ks-chart-container, .kiwi-sizing-recommender') || trigger.parentElement;
      if (widget && widget !== dest) dest.appendChild(widget);
    });
  }

  function setMediaTabIndex(root, disable) {
    if (!root) return;
    Array.prototype.forEach.call(root.querySelectorAll('iframe, video'), function (el) {
      if (disable) el.setAttribute('tabindex', '-1');
      else el.removeAttribute('tabindex');
    });
  }

  function inertClosedChrome() {
    Array.prototype.forEach.call(document.querySelectorAll(CLOSED_CHROME), function (el) {
      if (SUPPORTS_INERT) el.inert = true;
      else if (el.getAttribute('aria-hidden') !== 'true') el.setAttribute('aria-hidden', 'true');
      setMediaTabIndex(el, true);
    });
    // Inert would take the widget's own trigger out of hit-testing, so only silence its media.
    Array.prototype.forEach.call(document.querySelectorAll(INJECT), function (el) {
      setMediaTabIndex(el, true);
    });
  }

  function awakenModal(modal) {
    if (!modal) return;
    if (SUPPORTS_INERT) modal.inert = false;
    var backdrop = document.querySelector('.ks-modal-backdrop');
    if (backdrop && SUPPORTS_INERT) backdrop.inert = false;
    if (modal.getAttribute('aria-hidden') === 'true') modal.removeAttribute('aria-hidden');
    setMediaTabIndex(modal, true);
    patchModalControls();
  }

  function addsTrigger(record) {
    return Array.prototype.some.call(record.addedNodes, function (node) {
      if (node.nodeType !== 1) return false;
      return (node.matches && node.matches(TRIGGERS)) ||
        (node.querySelector && !!node.querySelector(TRIGGERS));
    });
  }

  function addsClosedMedia(record) {
    var selector = 'iframe, video, ' + CLOSED_CHROME + ', ' + INJECT;
    return Array.prototype.some.call(record.addedNodes, function (node) {
      if (node.nodeType !== 1) return false;
      if (node.matches && node.matches(selector)) return true;
      return node.querySelector && !!node.querySelector(selector);
    });
  }

  // Everything except the dialog is made inert, which removes it from the tab order and
  // from screen reader navigation. A Tab trap alone does not stop gesture navigation.
  function inertOutside(modal) {
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === modal || (child.matches && child.matches(KEEP_LIVE))) return;

      if (SUPPORTS_INERT) {
        if (child.inert) return;
        child.inert = true;
        inerted.push(child);
        return;
      }

      // Fallback: aria-hidden covers the screen reader half of what inert does. It
      // leaves these elements tabbable, which onKeydown is already responsible for.
      // Any existing value is preserved, since the theme sets it on its own overlays.
      if (child.getAttribute('aria-hidden') === 'true') return;
      hidden.push([child, child.getAttribute('aria-hidden')]);
      child.setAttribute('aria-hidden', 'true');
    });
  }

  function releaseInert() {
    inerted.forEach(function (el) { el.inert = false; });
    inerted = [];
    hidden.forEach(function (entry) {
      if (entry[1] === null) entry[0].removeAttribute('aria-hidden');
      else entry[0].setAttribute('aria-hidden', entry[1]);
    });
    hidden = [];
  }

  function onKeydown(event) {
    if (event.key !== 'Tab' || !openModal) return;

    var items = focusables(openModal);
    if (!items.length) {
      event.preventDefault();
      (openModal.querySelector(INNER) || openModal).focus();
      return;
    }

    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;

    if (items.indexOf(active) === -1) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    }
  }

  function open() {
    var modal = document.querySelector(MODAL);
    if (!modal || openModal === modal) return;
    openModal = modal;

    awakenModal(modal);
    inertOutside(modal);
    document.addEventListener('keydown', onKeydown);

    if (!modal.contains(document.activeElement)) {
      (modal.querySelector(INNER) || modal).focus();
    }
  }

  function close() {
    document.removeEventListener('keydown', onKeydown);
    releaseInert();
    inertClosedChrome();
    if (!openModal) return;
    openModal = null;
    if (lastTrigger && isVisible(lastTrigger)) lastTrigger.focus();
  }

  function sync() {
    if (document.documentElement.classList.contains(OPEN_CLASS)) open();
    else close();
  }

  function init() {
    // Remember which control opened the dialog, so focus can return there on close.
    document.addEventListener('click', function (event) {
      var t = event.target.closest && event.target.closest(TRIGGERS);
      if (t) lastTrigger = t;
    }, true);

    new MutationObserver(sync).observe(document.documentElement, {
      attributes: true, attributeFilter: ['class'],
    });

    // Kiwi re-injects its controls when the theme re-renders on variant change, so
    // newly added triggers have to be promoted again. Only mutations that actually add
    // a trigger schedule a scan, and a scan already scheduled is never rescheduled:
    // resetting the timer on every mutation would let a page that churns faster than
    // the delay (a carousel, live cart, predictive search) postpone it indefinitely
    // and leave the trigger unpatched.
    var pending = null;
    var queuedPatch = false;
    var queuedInert = false;
    new MutationObserver(function (records) {
      if (records.some(addsTrigger)) queuedPatch = true;
      if (records.some(addsClosedMedia)) queuedInert = true;
      if (pending !== null || (!queuedPatch && !queuedInert)) return;
      pending = setTimeout(function () {
        pending = null;
        if (queuedPatch) patchTriggers();
        if (document.documentElement.classList.contains(OPEN_CLASS)) {
          patchModalControls();
          setMediaTabIndex(document.querySelector(MODAL), true);
        } else {
          inertClosedChrome();
        }
        queuedPatch = false;
        queuedInert = false;
      }, 100);
    }).observe(document.body, { childList: true, subtree: true });

    patchTriggers();
    inertClosedChrome();
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
